import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownCircle, ArrowUpCircle, RotateCcw, PauseCircle, AlertTriangle, User } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
type TransactionType = 'funding' | 'release' | 'refund' | 'hold' | 'dispute';
interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  timestamp: string;
  performedBy: string;
  notes?: string;
}
interface EscrowTransactionLogProps {
  transactions: Transaction[];
}
const TRANSACTION_CONFIG: Record<TransactionType, {
  icon: React.ElementType;
  color: string;
  bgColor: string;
  label: string;
}> = {
  funding: {
    icon: ArrowDownCircle,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Escrow Funded'
  },
  release: {
    icon: ArrowUpCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Released to Landlord'
  },
  refund: {
    icon: RotateCcw,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    label: 'Refunded to Renta'
  },
  hold: {
    icon: PauseCircle,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Escrow Held'
  },
  dispute: {
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'Dispute Raised'
  }
};
export function EscrowTransactionLog({
  transactions
}: EscrowTransactionLogProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-NG', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-NG', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };
  if (transactions.length === 0) {
    return <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-id="element-1122">
        <PauseCircle className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-1123" />
        <p data-id="element-1124">No transactions yet</p>
      </div>;
  }
  return <div className="space-y-3" data-id="element-1125">
      {transactions.map((transaction, index) => {
      const config = TRANSACTION_CONFIG[transaction.type];
      const Icon = config.icon;
      const {
        date,
        time
      } = formatDate(transaction.timestamp);
      return <motion.div key={transaction.id} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: index * 0.05
      }} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-1126">
            {/* Icon */}
            <div className={`p-2 rounded-lg ${config.bgColor}`} data-id="element-1127">
              <Icon className={`h-5 w-5 ${config.color}`} data-id="element-1128" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-1129">
              <div className="flex items-start justify-between" data-id="element-1130">
                <div data-id="element-1131">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1132">
                    {config.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-0.5" data-id="element-1133">
                    <User className="h-3 w-3 mr-1" data-id="element-1134" />
                    {transaction.performedBy}
                  </p>
                </div>
                <div className="text-right" data-id="element-1135">
                  <p className={`text-sm font-bold ${config.color}`} data-id="element-1136">
                    {transaction.type === 'funding' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1137">
                    {date} • {time}
                  </p>
                </div>
              </div>

              {transaction.notes && <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600" data-id="element-1138">
                  "{transaction.notes}"
                </p>}
            </div>
          </motion.div>;
    })}
    </div>;
}